import { fetchJSON, getWpBaseUrl } from "../utils/api";

type OgImageValue =
  | string
  | number
  | {
      id?: number;
      url?: string;
      source_url?: string;
      sizes?: Record<string, string>;
      media_details?: {
        sizes?: Record<string, { source_url?: string }>;
      };
    };

type WPMedia = {
  source_url?: string;
  media_details?: {
    sizes?: Record<string, { source_url?: string }>;
  };
};

type WPPageBase = {
  title: { rendered: string };
  content: { rendered: string };
  excerpt?: { rendered: string };
  featured_media?: number;
  lang?: string;
  link?: string;
  _embedded?: {
    "wp:featuredmedia"?: Array<{
      source_url?: string;
      media_details?: {
        sizes?: Record<string, { source_url?: string }>;
      };
    }>;
  };
};

type WPPublicSeoFields = {
  seo_title?: string;
  seo_description?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  robots?: string;
  show_h1?: boolean;
  hide_h1_visually?: boolean;
  contact_form_enabled?: boolean;
  contact_recipient_email?: string;
  contact_form_title?: string;
  contact_form_intro?: string;
  contact_success_message?: string;
  contact_privacy_hint?: string;
  contact_submit_label?: string;
  newsletter_enabled?: boolean;
  newsletter_provider?: string;
  newsletter_group_id?: string;
  newsletter_form_id?: string;
  newsletter_default_tag?: string;
  newsletter_interest_label?: string;
  newsletter_interest_hint?: string;
  newsletter_interest_options?: string;
  newsletter_headline?: string;
  newsletter_intro?: string;
  newsletter_privacy_hint?: string;
  newsletter_button_label?: string;
  newsletter_success_message?: string;
  project_key?: ContentProjectKey;
  visibility_level?: ContentVisibilityLevel;
  required_access_key?: ContentAccessKey | "";
  content_listing_enabled?: boolean;
  content_teaser_override?: string;
  access_teaser_message?: string;
};

type WPRawSeoFields = Omit<WPPublicSeoFields, "og_image"> & {
  og_image?: OgImageValue;
};

export type ContentProjectKey = "core" | "moniuk" | "pulse" | "edge" | "game" | "bos" | "dwy";
export type ContentVisibilityLevel = "public" | "newsletter" | "paid" | "private";
export type ContentAccessKey =
  | "access_pulse"
  | "access_edge"
  | "access_game"
  | "access_bos_review"
  | "access_dwy_service";

export type WPPage = WPPageBase & {
  acf?: WPPublicSeoFields;
};

type WPPostBase = {
  id: number;
  slug: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content: { rendered: string };
  date: string;
  categories?: number[];
  lang?: string;
  link?: string;
  _embedded?: {
    "wp:featuredmedia"?: Array<{
      source_url?: string;
      media_details?: {
        sizes?: Record<string, { source_url?: string }>;
      };
    }>;
    author?: Array<{
      name?: string;
    }>;
    "wp:term"?: Array<
      Array<{
        id?: number;
        taxonomy?: string;
        name?: string;
        slug?: string;
      }>
    >;
  };
};

export type WPPost = WPPostBase & {
  acf?: WPPublicSeoFields;
};

type WPRawPage = WPPageBase & { acf?: WPRawSeoFields };
type WPRawPost = WPPostBase & { acf?: WPRawSeoFields };

export type WPNavNode = {
  id?: number;
  slug?: string;
  title?: { rendered?: string };
  content?: { rendered?: string };
  lang?: string;
};

export type WPCategory = {
  id: number;
  slug: string;
  name: string;
  lang?: string;
  link?: string;
  acf?: {
    show_h1?: boolean;
    hide_h1_visually?: boolean;
  };
};

const withLang = (path: string, lang?: string) => {
  if (!lang) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}lang=${encodeURIComponent(lang)}`;
};

const wpFetchJSON = async <T,>(path: string, options: RequestInit = {}) =>
  fetchJSON<T>(path, { ...options, baseUrl: getWpBaseUrl() });

const pickImageFromMedia = (media?: WPMedia | null) => {
  if (!media) return "";
  return (
    media.media_details?.sizes?.large?.source_url ||
    media.media_details?.sizes?.full?.source_url ||
    media.source_url ||
    ""
  );
};

const pickImageFromValue = (value: OgImageValue) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return "";
  return (
    value.url ||
    value.sizes?.large ||
    value.sizes?.full ||
    value.source_url ||
    value.media_details?.sizes?.large?.source_url ||
    value.media_details?.sizes?.full?.source_url ||
    ""
  );
};

const resolveOgImage = async (value?: OgImageValue): Promise<string> => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    try {
      const media = await wpFetchJSON<WPMedia>(`/wp/v2/media/${value}`);
      return pickImageFromMedia(media);
    } catch {
      return "";
    }
  }
  const direct = pickImageFromValue(value);
  if (direct) return direct;
  if (value.id) {
    try {
      const media = await wpFetchJSON<WPMedia>(`/wp/v2/media/${value.id}`);
      return pickImageFromMedia(media);
    } catch {
      return "";
    }
  }
  return "";
};

const withResolvedOgImage = async <T extends { acf?: WPRawSeoFields }>(
  item: T | null
): Promise<T | null> => {
  if (!item?.acf) return item;
  const baseAcf = normalizeContentClassification(item.acf);
  if (!item.acf.og_image) {
    return {
      ...item,
      acf: baseAcf
    };
  }
  const resolved = await resolveOgImage(item.acf.og_image);
  return {
    ...item,
    acf: {
      ...baseAcf,
      og_image: resolved
    }
  };
};

const normalizeProjectKey = (value?: string): ContentProjectKey => {
  switch (value) {
    case "moniuk":
    case "pulse":
    case "edge":
    case "game":
    case "bos":
    case "dwy":
      return value;
    default:
      return "core";
  }
};

const normalizeVisibilityLevel = (value?: string): ContentVisibilityLevel => {
  switch (value) {
    case "newsletter":
    case "paid":
    case "private":
      return value;
    default:
      return "public";
  }
};

const normalizeAccessKey = (value?: string): ContentAccessKey | "" => {
  switch (value) {
    case "access_pulse":
    case "access_edge":
    case "access_game":
    case "access_bos_review":
    case "access_dwy_service":
      return value;
    default:
      return "";
  }
};

const normalizeContentClassification = (acf: WPRawSeoFields): WPPublicSeoFields => ({
  ...acf,
  og_image: typeof acf.og_image === "string" ? acf.og_image : "",
  project_key: normalizeProjectKey(acf.project_key),
  visibility_level: normalizeVisibilityLevel(acf.visibility_level),
  required_access_key: normalizeAccessKey(acf.required_access_key),
  content_listing_enabled: acf.content_listing_enabled ?? true,
  content_teaser_override: acf.content_teaser_override || "",
  access_teaser_message: acf.access_teaser_message || ""
});

const matchesLang = (item: { lang?: string; link?: string }, lang?: string) => {
  if (!lang) return true;
  if (item.lang) return item.lang === lang;
  if (!item.link) return false;
  try {
    const path = new URL(item.link).pathname;
    const isEn = path === "/en" || path.startsWith("/en/");
    return lang === "en" ? isEn : !isEn;
  } catch {
    const isEn = item.link.includes("/en/");
    return lang === "en" ? isEn : !isEn;
  }
};

const pickByLang = <T extends { lang?: string; link?: string }>(
  items: T[],
  lang?: string,
  strict = false
) => {
  if (!lang) return items;
  const filtered = items.filter((item) => matchesLang(item, lang));
  if (filtered.length) return filtered;
  return strict ? [] : items;
};

export const getPageBySlug = async (slug: string, lang?: string) => {
  const data = await wpFetchJSON<WPRawPage[]>(
    withLang(`/wp/v2/pages?slug=${encodeURIComponent(slug)}&_embed`, lang)
  );
  const items = pickByLang(data || [], lang, true);
  const item = items?.[0] ?? null;
  return (await withResolvedOgImage(item)) as WPPage | null;
};

export const getPosts = async (perPage = 12, lang?: string) => {
  const data = await wpFetchJSON<WPRawPost[]>(
    withLang(`/wp/v2/posts?per_page=${perPage}&_embed`, lang)
  );
  return pickByLang(data || [], lang) as WPPost[];
};

export const getPostBySlug = async (slug: string, lang?: string) => {
  const data = await wpFetchJSON<WPRawPost[]>(
    withLang(`/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed`, lang)
  );
  const items = pickByLang(data || [], lang, true);
  const item = items?.[0] ?? null;
  return (await withResolvedOgImage(item)) as WPPost | null;
};

export const getCategoryBySlug = async (slug: string, lang?: string) => {
  const data = await wpFetchJSON<WPCategory[]>(
    withLang(`/wp/v2/categories?slug=${encodeURIComponent(slug)}`, lang)
  );
  const items = pickByLang(data || [], lang, true);
  return items?.[0] ?? null;
};

export const getPostsByCategory = async (categoryId: number, perPage = 12, lang?: string) => {
  const data = await wpFetchJSON<WPRawPost[]>(
    withLang(`/wp/v2/posts?categories=${categoryId}&per_page=${perPage}&_embed`, lang)
  );
  return pickByLang(data || [], lang) as WPPost[];
};

export const getMenu = async (perPage = 20, lang?: string) =>
  wpFetchJSON<WPNavNode[]>(withLang(`/wp/v2/navigation?per_page=${perPage}`, lang));

export const getContentProjectKey = (item?: { acf?: WPPublicSeoFields } | null): ContentProjectKey =>
  item?.acf?.project_key || "core";

export const getContentVisibilityLevel = (item?: {
  acf?: WPPublicSeoFields;
} | null): ContentVisibilityLevel => item?.acf?.visibility_level || "public";

export const getRequiredAccessKey = (item?: { acf?: WPPublicSeoFields } | null): ContentAccessKey | "" =>
  item?.acf?.required_access_key || "";

export const isContentListed = (item?: { acf?: WPPublicSeoFields } | null) =>
  item?.acf?.content_listing_enabled ?? true;

export const isContentVisibleForAccess = (
  item: { acf?: WPPublicSeoFields } | null | undefined,
  accessKeys: string[] = []
) => {
  const visibility = getContentVisibilityLevel(item);
  if (visibility === "public" || visibility === "newsletter") return true;

  const requiredAccessKey = getRequiredAccessKey(item);
  if (!requiredAccessKey) return false;

  return accessKeys.includes(requiredAccessKey);
};
